import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccountMembership } from '@mkp/account-membership/models';
import { Account } from '@mkp/account/models';
import { accountApiActions } from '@mkp/account/data-access/actions';
import { authActions, authOnboardingRecoveryActions } from '@mkp/auth/actions';
import { CompanyResource } from '@mkp/company/data-access';
import { CompanyProfile } from '@mkp/company/models';
import {
  companyApiActions,
  companyExistsGuardActions,
  companyFormPageActions,
} from '@mkp/company/state/actions';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { isMessageError, MessageError } from '@mkp/shared/data-access';
import { toUUID } from '@mkp/shared/models';
import { catch304 } from '@mkp/shared/util-api';
import { AppConfig } from '@mkp/shared/util-app-config';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { CompanyApiActions } from '../../actions/src/lib/company-api.actions';
import {
  selectCompanyById,
  selectFirstCompanyByAccountId,
} from './selectors/company-profile.selectors';

@Injectable()
export class CompanyApiEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly companyResource = inject(CompanyResource);
  private readonly router = inject(Router);

  readonly updateCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(companyFormPageActions.updateCompany),
      switchMap(({ company }) => {
        return this.companyResource.updateCompany(company).pipe(
          map((updatedCompany) =>
            companyApiActions.updateCompanySuccess({ company: updatedCompany })
          ),
          // Has to run before catchError: isCompanyApiError accepts every error, so a 304
          // reaching it would be reported as a failed save.
          catch304(() => companyApiActions.updateCompanyNoChanges({ companyId: company.id })),
          catchError((error: unknown) =>
            isCompanyApiError(error)
              ? of(companyApiActions.updateCompanyFailure({ companyId: company.id, error }))
              : EMPTY
          )
        );
      })
    );
  });

  readonly loadCompanyIfNotExist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(companyExistsGuardActions.canActivate),
      concatLatestFrom(({ companyId }) => this.store.select(selectCompanyById(companyId))),
      filter(([, company]) => !company),
      switchMap(([{ companyId }]) => {
        return this.companyResource.getCompany(companyId).pipe(
          map((company) => companyApiActions.loadCompanyIfNotExistSuccess({ company })),
          catchError((error: unknown) =>
            isCompanyApiError(error)
              ? of(
                  companyApiActions.loadCompanyIfNotExistFailure({
                    companyId: toUUID(companyId),
                    error,
                  })
                )
              : EMPTY
          )
        );
      })
    );
  });

  readonly loadCompaniesAtLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.loadActiveAccountMembershipsSuccess),
      filter(({ accountMemberships }) => accountMemberships.length > 0),
      switchMap(({ accountMemberships }) =>
        this.companyResource
          .getCompanies({
            params: {
              ...getFilterForAccountMembership(accountMemberships),
              limit: COMPANY_DETECTION_PAGE_LIMIT,
            },
            context: getOptionalContext(),
          })
          .pipe(
            switchMap(({ companyProfiles }) =>
              from(getCompaniesAtLoginActions(accountMemberships, companyProfiles))
            ),
            catchError((error: unknown) =>
              isCompanyApiError(error)
                ? of(authActions.loadCompaniesAfterLoginFailed({ error }))
                : EMPTY
            )
          )
      )
    );
  });

  readonly handleNoCompaniesFoundOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.noCompaniesFoundAfterLogin),
      map(({ accountMemberships }) =>
        authOnboardingRecoveryActions.onboardingFailedFromCompany({
          accountIds: accountMemberships.map(({ account }) => account.id),
        })
      )
    )
  );

  // when a user deletes their own roles we need to update all the companies
  readonly refreshAllCompanies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(accountApiActions.refreshAllAccountsSuccess),
      filter(({ accounts }) => accounts.length > 0),
      switchMap(({ accounts }) =>
        this.companyResource.getCompanies(getFilterForAccounts(accounts)).pipe(
          map(({ companyProfiles }) =>
            CompanyApiActions.refreshAllCompaniesSuccess({ companyProfiles })
          ),
          catchError((error: unknown) =>
            isCompanyApiError(error)
              ? of(CompanyApiActions.refreshAllCompaniesFailure({ error }))
              : EMPTY
          )
        )
      )
    );
  });

  readonly fetchAndSetCompanies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.workspaceFoundInStore, authActions.loadWorkspaceAccountSuccess),
      concatLatestFrom(({ accounts: [{ id }] }) =>
        this.store.select(selectFirstCompanyByAccountId(id))
      ),
      switchMap(
        ([
          {
            accounts: [{ legalEntity }],
          },
          maybeCompanyProfile,
        ]) => {
          return maybeCompanyProfile
            ? of(
                authActions.loadWorkspaceCompanySuccess({
                  companyProfiles: [maybeCompanyProfile],
                })
              )
            : this.companyResource
                .getCompanies({ filter: `legalEntity.id==${legalEntity.id}` })
                .pipe(
                  map(({ companyProfiles }) =>
                    authActions.loadWorkspaceCompanySuccess({ companyProfiles })
                  ),
                  catchError((error: unknown) => {
                    this.router.navigate([AppConfig.routes.dashboard]);
                    return isCompanyApiError(error)
                      ? of(authActions.loadWorkspaceCompanyError({ error }))
                      : EMPTY;
                  })
                );
        }
      )
    );
  });
}

const isCompanyApiError = (error: unknown): error is MessageError =>
  isMessageError(error, 'CompanyApiEffects');

// Login carries at most 200 memberships, each resolving to a single legal entity.
const COMPANY_DETECTION_PAGE_LIMIT = 500;

// Profiles that were found still have to reach the reducer when a sibling workspace is missing one.
const getCompaniesAtLoginActions = (
  accountMemberships: AccountMembership[],
  companyProfiles: CompanyProfile[]
): Action[] => {
  const legalEntityIdsWithProfile = new Set<string>(
    companyProfiles.flatMap(({ legalEntityId }) => (legalEntityId ? [legalEntityId] : []))
  );
  const membershipsWithoutProfile = accountMemberships.filter(
    ({ account }) => !account.legalEntityId || !legalEntityIdsWithProfile.has(account.legalEntityId)
  );

  return [
    ...(companyProfiles.length
      ? [authActions.loadCompaniesAfterLoginSuccess({ companyProfiles })]
      : []),
    ...(membershipsWithoutProfile.length
      ? [authActions.noCompaniesFoundAfterLogin({ accountMemberships: membershipsWithoutProfile })]
      : []),
  ];
};

const getFilterForAccountMembership = (accountMemberships: AccountMembership[]) => ({
  filter: accountMemberships
    .map((accountMembership) => `legalEntity.id==${accountMembership.account.legalEntityId}`)
    .join(','),
});

const getFilterForAccounts = (accounts: Account[]) => ({
  filter: accounts.map(({ id }) => `legalEntity.accounts.id==${id}`).join(','),
});
